/**
 * HlsLive — sidebar-mounted live tracking of all active HLS streams.
 *
 * Two responsibilities:
 *   1. Maintain a live count of active sessions for the sidebar badge.
 *      The count is derived from session.start / session.stop events on
 *      the hlsBus (no polling).
 *   2. Render a click-to-open drawer that lists all active sessions with
 *      Username · Filename · Position. The Node SSE bridge has already
 *      privacy-filtered the payload — non-owner / non-admin connections
 *      receive null for media_basename and state_detail, so we can safely
 *      render `b.media_basename ?? '—'` without leaking anything.
 *
 * Initial seed for the drawer comes from GET /api/hls/list (returns the
 * same privacy-filtered shape as the SSE events). Live updates are
 * applied delta-style on top of that snapshot.
 *
 * Plan-ref: /root/.claude/plans/check-deinen-plan-nochmal-snazzy-stream.md
 */

import { hlsBus } from '../core/hls_bus.js';
import { systemBus } from '../core/system_bus.js';
import { api } from '../core/api.js';
import { auth } from '../core/auth.js';

/** Active sessions keyed by session_hash → row object (the latest seen). */
const sessions = new Map();
let drawerEl = null;
let outsideClickHandler = null;
let countCb = null; // (n:number) => void

/**
 * Wire up the live tracker.
 *   - subscribes to hlsBus
 *   - returns a cleanup() function
 *
 * @param {(n:number)=>void} onCount called whenever the active-stream count changes
 */
export function attachHlsLive({ onCount } = {}) {
  countCb = onCount || null;

  hlsBus.start();
  // Phase E1.2: system-channel publishes the full PlaybackSession
  // dumpJson every 1 s. We use it as a continuous reload-resilient
  // seed — every tick refreshes current_sec / state for sessions
  // already in our map and adds new ones the live hls-channel may
  // have missed (e.g. session.start fired before this tab opened).
  systemBus.start();

  const unsub = hlsBus.subscribe(
    () => true, // accept all events; we filter below
    (evt) => {
      if (!evt || evt.source !== 'hls') return;
      const d = evt.data || {};
      const h = String(d.session_hash || '');
      if (!h) return;

      if (evt.event === 'session.stop') {
        sessions.delete(h);
      } else {
        // Merge: keep media_basename if a prior event already had it (the
        // first session.start carries it for owner/admin; redacted clients
        // see null and the merge keeps null — that's the privacy intent).
        const prev = sessions.get(h) || {};
        sessions.set(h, { ...prev, ...d });
      }
      emitCount();
      renderDrawerIfOpen();
    },
  );

  // Phase E1.2: system-bus subscriber merges zapd-direct sessions and
  // their live current_sec/state. dumpJson exposes the full publicPayload
  // shape (session_hash, user_id, media_basename, state, current_sec,
  // duration_sec) for every active zapd session; we merge field-by-field
  // so live hls-bus events still take precedence on `state` transitions.
  const unsubSystem = systemBus.subscribe((evt) => {
    const list = evt?.data?.sessions;
    if (!Array.isArray(list)) return;
    let changed = false;
    const seen = new Set();
    for (const s of list) {
      const h = String(s.session_hash || s.sid || '');
      if (!h) continue;
      seen.add(h);
      const prev = sessions.get(h);
      if (!prev) {
        sessions.set(h, s);
        changed = true;
      } else {
        // Merge: keep prev's state if a session.phase event already
        // moved it past `probing`; otherwise let system-snapshot win.
        const merged = { ...prev, ...s };
        // Only flip count-changed when meaningful fields update.
        if (prev.current_sec !== merged.current_sec || prev.state !== merged.state) {
          changed = true;
        }
        sessions.set(h, merged);
      }
    }
    // Drop sessions that disappeared from zapd (= they ended). The
    // hls-bus session.stop event would have done this too, but we
    // can be belt-and-suspenders here.
    for (const h of [...sessions.keys()]) {
      if (!seen.has(h)) {
        // Only drop if the entry came from zapd (sid matches). PHP-DB
        // sessions live in their own namespace and wouldn't be in
        // /api/system/stats; we recognise zapd-origins by the presence
        // of `output_dir` (only dumpJson sets that).
        const e = sessions.get(h);
        if (e && e.output_dir) {
          sessions.delete(h);
          changed = true;
        }
      }
    }
    if (changed) {
      emitCount();
      renderDrawerIfOpen();
    }
  });

  return () => {
    try {
      unsub();
    } catch {}
    try {
      unsubSystem();
    } catch {}
    closeDrawer();
    countCb = null;
  };
}

function emitCount() {
  if (countCb) {
    try {
      countCb(sessions.size);
    } catch {}
  }
}

/** Toggle the drawer (called from the sidebar click handler). */
export function toggleHlsDrawer(anchorEl) {
  if (drawerEl) {
    closeDrawer();
    return;
  }
  openDrawer(anchorEl);
}

function openDrawer(anchorEl) {
  drawerEl = document.createElement('div');
  drawerEl.className = 'zap-hls-drawer';
  drawerEl.setAttribute('role', 'dialog');
  drawerEl.setAttribute('aria-label', 'Active HLS streams');
  document.body.appendChild(drawerEl);
  positionDrawer(anchorEl);
  renderDrawer();

  // Close on outside-click + Esc.
  outsideClickHandler = (e) => {
    if (drawerEl && !drawerEl.contains(e.target) && !anchorEl?.contains?.(e.target)) {
      closeDrawer();
    }
  };
  setTimeout(() => document.addEventListener('click', outsideClickHandler), 0);
  document.addEventListener('keydown', escClose);
}

function escClose(e) {
  if (e.key === 'Escape') closeDrawer();
}

function closeDrawer() {
  if (!drawerEl) return;
  drawerEl.remove();
  drawerEl = null;
  if (outsideClickHandler) {
    document.removeEventListener('click', outsideClickHandler);
    outsideClickHandler = null;
  }
  document.removeEventListener('keydown', escClose);
}

function positionDrawer(anchorEl) {
  if (!drawerEl) return;
  const r = anchorEl?.getBoundingClientRect?.();
  if (r) {
    // Sidebar nav-items are on the left; drawer anchors to the item's right edge.
    drawerEl.style.position = 'fixed';
    drawerEl.style.left = r.right + 8 + 'px';
    drawerEl.style.top = Math.max(8, r.top) + 'px';
  } else {
    drawerEl.style.position = 'fixed';
    drawerEl.style.left = '240px';
    drawerEl.style.top = '80px';
  }
}

function renderDrawerIfOpen() {
  if (!drawerEl) return;
  renderDrawer();
}

function renderDrawer() {
  if (!drawerEl) return;
  const list = Array.from(sessions.values()).sort((a, b) => {
    return String(a.started_at || '').localeCompare(String(b.started_at || ''));
  });
  const myUid = parseInt(auth.getUser?.()?.id ?? 0, 10) || null;

  if (list.length === 0) {
    drawerEl.innerHTML = `
      <header class="zap-hls-drawer__h">Active streams (0)</header>
      <div class="zap-hls-drawer__empty">Niemand streamt gerade.</div>`;
    return;
  }

  const rows = list
    .map((s) => {
      const uid = parseInt(s.user_id, 10) || 0;
      const username = String(s.username || 'user#' + uid);
      const filename = s.media_basename ? String(s.media_basename) : '—';
      const cur = formatHms(parseInt(s.current_sec, 10) || 0);
      const dur = s.duration_sec != null ? formatHms(parseInt(s.duration_sec, 10) || 0) : null;
      const pos = dur ? `${cur} / ${dur}` : cur;
      const phase = String(s.state || 'unknown');
      const isMine = myUid && uid === myUid;
      const phaseTag =
        phase !== 'playing'
          ? `<span class="zap-hls-drawer__phase" data-phase="${escAttr(phase)}">${escHtml(phase)}</span>`
          : '';
      const stopBtn = isMine
        ? `<button class="zap-hls-drawer__stop" data-stop="${escAttr(s.session_hash)}" title="Stream beenden">×</button>`
        : '';
      return `
      <li class="zap-hls-drawer__row${isMine ? ' is-mine' : ''}">
        <span class="zap-hls-drawer__user">${escHtml(username)}</span>
        <span class="zap-hls-drawer__file" title="${escAttr(filename)}">${escHtml(filename)}</span>
        <span class="zap-hls-drawer__pos">${escHtml(pos)}</span>
        ${phaseTag}
        ${stopBtn}
      </li>`;
    })
    .join('');

  drawerEl.innerHTML = `
    <header class="zap-hls-drawer__h">Active streams (${list.length})</header>
    <ul class="zap-hls-drawer__list">${rows}</ul>`;

  drawerEl.querySelectorAll('[data-stop]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const h = btn.getAttribute('data-stop');
      if (!h) return;
      api(`/api-zapd/play/${encodeURIComponent(h)}/stop`, { method: 'POST' }).catch((e) =>
        console.error('[hls-live] stop signal failed', h, e),
      );
      sessions.delete(h);
      emitCount();
      renderDrawer();
    });
  });
}

// ── helpers ─────────────────────────────────────────────────────────────────

function formatHms(s) {
  s = Math.max(0, Math.floor(s));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  return h > 0 ? `${h}:${pad2(m)}:${pad2(ss)}` : `${m}:${pad2(ss)}`;
}
function pad2(n) {
  return n < 10 ? '0' + n : '' + n;
}
function escHtml(s) {
  return String(s ?? '').replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c],
  );
}
function escAttr(s) {
  return escHtml(s);
}
